<template>
  <div class="flex flex-col items-center text-center pb-4">
    <h5 class="text-lg font-semibold mb-4">{{ formTitle }}</h5>
  </div>

  <div class="sm:max-w-xl w-full">
    <label class="font-semibold mb-2 block">Receiver’s Name<span class="text-orange-500">*</span></label>
    <input v-model="receiverName" @blur="validateReceiverName" placeholder="Enter receiver’s name"
      class="w-full p-3 border rounded-lg focus:outline-none text-sm sm:text-base mb-2"
      :class="{ 'border-red-500': errors.receiverName }" />
    <p v-if="errors.receiverName" class="flex items-start gap-1 text-red-500 text-xs">
      <svg class="w-3 h-3 mt-[2px]" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M9.4 3.4 1.8 16a1 1 0 0 0 .87 1.5h14.66a1 1 0 0 0 .87-1.5L10.6 3.4a1 1 0 0 0-1.73 0Z"
          stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
        <path d="M10 7.5v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
        <circle cx="10" cy="13.5" r="0.8" fill="currentColor" />
      </svg>
      {{ errors.receiverName }}
    </p>
  </div>

  <div class="sm:max-w-xl w-full">
    <label class="font-semibold mb-2 block">Receiver’s Email<span class="text-orange-500">*</span></label>
    <input v-model="recipientEmail" type="email" @blur="validateRecipientEmail" placeholder="Enter receiver’s email"
      class="w-full p-3 border rounded-lg focus:outline-none text-sm sm:text-base mb-2"
      :class="{ 'border-red-500': errors.recipientEmail }" />
    <p v-if="errors.recipientEmail" class="flex items-start gap-1 text-red-500 text-xs">
      <svg class="w-3 h-3 mt-[2px]" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M9.4 3.4 1.8 16a1 1 0 0 0 .87 1.5h14.66a1 1 0 0 0 .87-1.5L10.6 3.4a1 1 0 0 0-1.73 0Z"
          stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
        <path d="M10 7.5v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
        <circle cx="10" cy="13.5" r="0.8" fill="currentColor" />
      </svg>
      {{ errors.recipientEmail }}
    </p>
  </div>

  <div class="sm:max-w-xl w-full">
    <label class="font-semibold mb-2 block">Receiver’s Mobile Number<span class="text-orange-500">*</span></label>
    <input v-model="recipientPhone" type="tel" maxlength="10" @input="onPhoneInput" @blur="validateRecipientPhone"
      placeholder="Enter 10 digit mobile number"
      class="w-full p-3 border rounded-lg focus:outline-none text-sm sm:text-base mb-2"
      :class="{ 'border-red-500': errors.recipientPhone }" />
    <p v-if="errors.recipientPhone" class="flex items-start gap-1 text-red-500 text-xs">
      <svg class="w-3 h-3 mt-[2px]" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M9.4 3.4 1.8 16a1 1 0 0 0 .87 1.5h14.66a1 1 0 0 0 .87-1.5L10.6 3.4a1 1 0 0 0-1.73 0Z"
          stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
        <path d="M10 7.5v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
        <circle cx="10" cy="13.5" r="0.8" fill="currentColor" />
      </svg>
      {{ errors.recipientPhone }}
    </p>
  </div>

  <div class="sm:max-w-xl w-full">
    <label class="font-semibold mb-2 block">Message <span class="text-gray-400">(optional)</span></label>
    <textarea v-model="message" placeholder="Enter your message here..." rows="3" maxlength="300"
      class="w-full p-3 border rounded-lg focus:outline-none text-sm sm:text-base"></textarea>
  </div>

  <button class="w-full sm:max-w-xl mt-4 mb-3 py-3 bg-[#0C5542] text-white font-bold rounded-lg"
    @click="validateForm">
    SAVE DETAILS
  </button>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue'

const props = defineProps(['data'])
const emit = defineEmits(['close', 'success'])

const mode = computed(() => props.data?.mode || 'add')

const formTitle = computed(() =>
  mode.value === 'edit' ? 'Edit Receiver Details' : 'Add Receiver Details'
)

const receiverName = ref('')
const recipientEmail = ref('')
const recipientPhone = ref('')
const message = ref('')
const errors = ref({})

// Input sanitization for phone
const onPhoneInput = (event) => {
  recipientPhone.value = event.target.value.replace(/\D/g, '').slice(0, 10)
}

// Individual field validation functions
const validateReceiverName = () => {
  if (!receiverName.value.trim()) {
    errors.value.receiverName = 'Please enter receiver name'
  } else {
    errors.value.receiverName = ''
  }
}

const validateRecipientEmail = () => {
  if (!recipientEmail.value || !/\S+@\S+\.\S+/.test(recipientEmail.value)) {
    errors.value.recipientEmail = 'Please enter a valid email address'
  } else {
    errors.value.recipientEmail = ''
  }
}

const validateRecipientPhone = () => {
  if (!/^\d{10}$/.test(recipientPhone.value)) {
    errors.value.recipientPhone = 'Please enter a valid 10-digit mobile number'
  } else {
    errors.value.recipientPhone = ''
  }
}

// Watchers to clear errors in real-time when user corrects the input
watch(receiverName, () => {
  if (receiverName.value.trim()) errors.value.receiverName = ''
})

watch(recipientEmail, () => {
  if (/\S+@\S+\.\S+/.test(recipientEmail.value)) errors.value.recipientEmail = ''
})

watch(recipientPhone, () => {
  if (/^\d{10}$/.test(recipientPhone.value)) errors.value.recipientPhone = ''
})

// Final validation and submission
const validateForm = () => {
  validateReceiverName()
  validateRecipientEmail()
  validateRecipientPhone()

  if (Object.values(errors.value).some((err) => err)) return

  const giftItemData = props.data

  giftItemData.receiverName = receiverName.value.trim()
  giftItemData.recipientEmail = recipientEmail.value.trim()
  giftItemData.recipientPhone = Number(String(recipientPhone.value).trim());
  giftItemData.message = message.value.trim()

  emit('success', giftItemData)
}

onMounted(() => {
  if (props.data) {
    receiverName.value = props.data.receiverName || ''
    recipientEmail.value = props.data.recipientEmail || ''
    recipientPhone.value = props.data.recipientPhone || ''
    message.value = props.data.message || ''
  }
})
</script>
